import { AnalyticsContextValue } from "@swiggy-private/checkout-core";
import { SCREEN_NAME, CLICK_ACTIONS, IMPRESSION_ACTIONS } from "../constants";

interface OneTapPlanEvent {
    title: string;
    ctaText: string;
    subtitle: string;
    userSuperStatus: string;
    planId: string;
    planName: string;
    planPrice: string;
}

interface RemovePlanConfirmationImpression {
    savingsValue: string;
    userSuperStatus: string;
    title: string;
    message: string;
    subMessage: string;
    planId?: string;
    variant?: string;
    initialBillAmount?: string;
    finalBillAmount?: string;
}

interface ChangePlanEvent {
    planId: string;
    userSuperStatus: string;
    planPrice: string;
    planName: string;
    isOneTap: boolean;
}

interface RemovePlanEvent {
    userSuperStatus: string;
    planName: string;
    planId: string;
    cta: string;
    variant?: string;
    initialBillAmount?: string;
    finalBillAmount?: string;
}

export const clickCartSuperNudge = (
    payload: {
        title?: string;
        ctaText?: string;
        ctaLink?: string;
        subtitle?: string;
        userSuperStatus: string;
    },
    clickEvent: AnalyticsContextValue["clickEvent"],
): void => {
    const { userSuperStatus, title = "", subtitle = "", ctaText = "", ctaLink = "" } = payload;
    clickEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: CLICK_ACTIONS.CLICK_CART_SUPER_NUDGE,
        context: JSON.stringify({ userSuperStatus, title, ctaLink, subtitle, ctaText }),
    });
};

export const clickRemoveSuper = (
    payload: {
        planId: string;
        userSuperStatus: string;
        planPrice: string;
        planName: string;
    },
    clickEvent: AnalyticsContextValue["clickEvent"],
): void => {
    const { planId, userSuperStatus, planPrice, planName } = payload;
    clickEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: CLICK_ACTIONS.CLICK_REMOVE_SUPER,
        context: JSON.stringify({ userSuperStatus, planId, planPrice, planName }),
    });
};

export const clickOneTapAddPlan = (payload: OneTapPlanEvent, clickEvent: AnalyticsContextValue["clickEvent"]): void => {
    clickEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: CLICK_ACTIONS.CLICK_CART_SUPER_NUDGE_ONE_TAP_ADD,
        context: JSON.stringify(payload),
    });
};

export const impressionSuperNudge = (
    data: { title: string; ctaText: string; subtitle: string; userSuperStatus: string; link: string },
    impressionEvent: AnalyticsContextValue["impressionEvent"],
): void => {
    const { title, ctaText, subtitle, userSuperStatus, link } = data;
    impressionEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: IMPRESSION_ACTIONS.IMPRESSION_CART_SUPER_NUDGE,
        context: JSON.stringify({ userSuperStatus, title, ctaText, subtitle, link }),
    });
};

export const impressionFooterStrip = (
    data: { userSuperStatus: string; title: string; ctaText: string },
    impressionEvent: AnalyticsContextValue["impressionEvent"],
): void => {
    const { userSuperStatus, title, ctaText } = data;
    impressionEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: IMPRESSION_ACTIONS.IMPRESSION_LIFETIME_SAVINGS_CALLOUT,
        context: JSON.stringify({ userSuperStatus, title, ctaText }),
    });
};

export const impressionOneTapAddNudge = (
    payload: OneTapPlanEvent,
    impressionEvent: AnalyticsContextValue["impressionEvent"],
): void => {
    impressionEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: IMPRESSION_ACTIONS.IMPRESSION_CART_SUPER_NUDGE_ONE_TAP,
        context: JSON.stringify(payload),
    });
};

export const impressionSuperChangePlan = (
    data: {
        planName: string;
        planId: string;
        userSuperStatus: string;
        planPrice: string;
        benefits: string;
        ctaLink: string;
    },
    impressionEvent: AnalyticsContextValue["impressionEvent"],
): void => {
    const { userSuperStatus, planId, planPrice, planName, benefits, ctaLink } = data;
    impressionEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: IMPRESSION_ACTIONS.IMPRESSION_CHANGE_PLAN,
        context: JSON.stringify({ userSuperStatus, planId, planPrice, planName, benefits, ctaLink }),
    });
};

export const clickChangeSuperPlan = (
    payload: ChangePlanEvent,
    clickEvent: AnalyticsContextValue["clickEvent"],
): void => {
    clickEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: CLICK_ACTIONS.CLICK_CART_SUPER_CHANGE_PLAN,
        context: JSON.stringify(payload),
    });
};

export const impressionSuperOfferBadge = (
    offerText: string,
    userSuperStatus: string,
    impressionEvent: AnalyticsContextValue["impressionEvent"],
): void => {
    impressionEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: IMPRESSION_ACTIONS.IMPRESSION_CART_SUPER_OFFER_BADGE,
        context: JSON.stringify({ userSuperStatus, offerText }),
    });
};

export const impressionSuperRemovePlanConfirmationHalfCard = (
    payload: RemovePlanConfirmationImpression,
    impressionEvent: AnalyticsContextValue["impressionEvent"],
): void => {
    impressionEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: IMPRESSION_ACTIONS.IMPRESSION_SUPER_REMOVE_PLAN_CONFIRMATION_HALF_CARD,
        context: JSON.stringify(payload),
    });
};

export const impressionSuperRemovePlanConfirmationBackButton = (
    payload: RemovePlanEvent,
    impressionEvent: AnalyticsContextValue["impressionEvent"],
): void => {
    impressionEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: IMPRESSION_ACTIONS.IMPRESSION_SUPER_REMOVE_PLAN_HALF_CARD_BACK_BUTTON,
        context: JSON.stringify(payload),
    });
};

export const impressionSuperRemovePlanConfirmationRemoveButton = (
    payload: RemovePlanEvent,
    impressionEvent: AnalyticsContextValue["impressionEvent"],
): void => {
    impressionEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: IMPRESSION_ACTIONS.IMPRESSION_SUPER_REMOVE_PLAN_HALF_CARD_REMOVE_BUTTON,
        context: JSON.stringify(payload),
    });
};

export const clickSuperRemovePlanHalfCardBackButton = (
    payload: RemovePlanEvent,
    clickEvent: AnalyticsContextValue["clickEvent"],
): void => {
    clickEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: CLICK_ACTIONS.CLICK_SUPER_REMOVE_PLAN_HALF_CARD_BACK_BUTTON,
        context: JSON.stringify(payload),
    });
};

export const clickSuperRemovePlanHalfCardRemoveButton = (
    payload: RemovePlanEvent,
    clickEvent: AnalyticsContextValue["clickEvent"],
): void => {
    clickEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: CLICK_ACTIONS.CLICK_SUPER_REMOVE_PLAN_HALF_CARD_REMOVE_BUTTON,
        context: JSON.stringify(payload),
    });
};

export const impressionSuperViewDetailsFooter = (
    ctaText: string,
    impressionEvent: AnalyticsContextValue["impressionEvent"],
): void => {
    impressionEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: IMPRESSION_ACTIONS.IMPRESSION_SUPER_VIEW_DETAILS_FOOTER,
        label: ctaText,
    });
};

export const clickSuperViewDetailsFooterCTA = (
    ctaText: string,
    clickEvent: AnalyticsContextValue["clickEvent"],
): void => {
    clickEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: CLICK_ACTIONS.CLICK_SUPER_VIEW_DETAILS_FOOTER,
        label: ctaText,
    });
};

export const impressionSuperViewAllBenefitsCTA = (
    userSuperStatus: string,
    contextData: Record<string, string | undefined>,
    impressionEvent: AnalyticsContextValue["impressionEvent"],
): void => {
    impressionEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: IMPRESSION_ACTIONS.IMPRESSION_SUPER_VIEW_ALL_BENEFITS_CTA,
        context: JSON.stringify({ userSuperStatus, ...contextData }),
    });
};

export const clickSuperViewAllBenefitsCTA = (
    userSuperStatus: string,
    contextData: Record<string, string | undefined>,
    clickEvent: AnalyticsContextValue["clickEvent"],
): void => {
    clickEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: CLICK_ACTIONS.CLICK_SUPER_VIEW_ALL_BENEFITS_CTA,
        context: JSON.stringify({ userSuperStatus, ...contextData }),
    });
};
// extra offer analytics events ---->
export const impressionSwiggyOneSavingsNudge = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    benefits: string,
    totalSavings?: string,
): void => {
    impressionEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: IMPRESSION_ACTIONS.IMPRESSION_SWIGGY_ONE_SAVINGS,
        value: JSON.stringify({ benefits, totalSavings: totalSavings || "" }),
    });
};

export const impressionSwiggyOneEOCompletioNudge = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    benefits: string,
    restaurantId: string,
): void => {
    impressionEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: IMPRESSION_ACTIONS.IMPRESSION_SWIGGY_ONE_EO_COMPLETION_NUDGE,
        value: benefits,
        context: JSON.stringify({ restaurantId, benefits }),
    });
};

export const clickSwiggyOneEOCompletioNudge = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    benefits: string,
    restaurantId: string,
): void => {
    clickEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: IMPRESSION_ACTIONS.CLICK_SWIGGY_ONE_EO_COMPLETION_NUDGE,
        value: benefits,
        context: JSON.stringify({ restaurantId, benefits }),
    });
};
// extra offer analytics events ---->
